import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type { ITimeOffEntry, ITimeOffEntryFilters, ITimeOffEntryPayload } from "@/types/time-off";

type TRawTimeOffEntry = Omit<ITimeOffEntry, "blocked_hours"> & {
  blocked_hours: number | string;
};

export interface ITimeOffEntriesPage {
  results: ITimeOffEntry[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  count: number;
}

export interface ITimeOffEntriesListQuery {
  cursor?: string | null;
  q?: string;
  member?: string[];
  order_by?: string;
  per_page?: number;
}

function normalizeTimeOffEntry(entry: TRawTimeOffEntry): ITimeOffEntry {
  const blockedHours =
    typeof entry.blocked_hours === "string"
      ? Number.parseFloat(entry.blocked_hours)
      : entry.blocked_hours;

  return {
    ...entry,
    blocked_hours: Number.isFinite(blockedHours) ? blockedHours : 0,
  };
}

export class TimeOffService extends ApiService {
  async listEntries(slug: string, filters: ITimeOffEntryFilters = {}): Promise<ITimeOffEntry[]> {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""),
    );
    const raw = await this.get<TRawTimeOffEntry[]>(`/api/workspaces/${slug}/time-off/`, {
      params,
    });
    return raw.map(normalizeTimeOffEntry);
  }

  /** Paginated entries fetch for the settings list (server filters/sort +
   *  cursor pagination). Distinct from ``listEntries`` (the full set the My Time
   *  Off panel + capacity views fetch). */
  async listEntriesPage(
    slug: string,
    options: ITimeOffEntriesListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<ITimeOffEntriesPage> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 50));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const m of options.member ?? []) params.append("member", m);
    const page = await this.get<
      Omit<ITimeOffEntriesPage, "results"> & { results: TRawTimeOffEntry[] }
    >(`/api/workspaces/${slug}/time-off/?${params.toString()}`, config);
    return { ...page, results: page.results.map(normalizeTimeOffEntry) };
  }

  /**
   * Export the Time Off list as CSV, scoped to the same filters/sort as the list
   * (`options`) and, when `body.ids` is set, restricted to those entries.
   */
  async exportTimeOff(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    options: { q?: string; member?: string[]; order_by?: string } = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const m of options.member ?? []) params.append("member", m);
    const qs = params.toString();
    return this.postRaw(`/api/workspaces/${slug}/export-time-off/${qs ? `?${qs}` : ""}`, body);
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  async listMyEntries(
    slug: string,
    filters: ITimeOffEntryFilters = {},
    config?: ApiRequestConfig,
  ): Promise<ITimeOffEntry[]> {
    const params = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => key !== "user_id" && value !== undefined && value !== "",
      ),
    );
    const raw = await this.get<TRawTimeOffEntry[]>(`/api/workspaces/${slug}/time-off/me/`, {
      ...config,
      params,
    });
    return raw.map(normalizeTimeOffEntry);
  }

  async createEntry(slug: string, data: ITimeOffEntryPayload): Promise<ITimeOffEntry> {
    const raw = await this.post<TRawTimeOffEntry>(`/api/workspaces/${slug}/time-off/`, data);
    return normalizeTimeOffEntry(raw);
  }

  async createMyEntry(slug: string, data: ITimeOffEntryPayload): Promise<ITimeOffEntry> {
    const raw = await this.post<TRawTimeOffEntry>(`/api/workspaces/${slug}/time-off/me/`, data);
    return normalizeTimeOffEntry(raw);
  }

  async updateEntry(
    slug: string,
    entryId: string,
    data: Partial<ITimeOffEntryPayload>,
  ): Promise<ITimeOffEntry> {
    const raw = await this.patch<TRawTimeOffEntry>(
      `/api/workspaces/${slug}/time-off/${entryId}/`,
      data,
    );
    return normalizeTimeOffEntry(raw);
  }

  async updateMyEntry(
    slug: string,
    entryId: string,
    data: Partial<ITimeOffEntryPayload>,
  ): Promise<ITimeOffEntry> {
    const raw = await this.patch<TRawTimeOffEntry>(
      `/api/workspaces/${slug}/time-off/me/${entryId}/`,
      data,
    );
    return normalizeTimeOffEntry(raw);
  }

  async deleteEntry(slug: string, entryId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/time-off/${entryId}/`);
  }

  async deleteMyEntry(slug: string, entryId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/time-off/me/${entryId}/`);
  }
}
